"use client";

import type React from "react";
import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  Box,
  Button,
  Flex,
  IconButton,
  Text,
  Textarea,
  VStack,
  Accordion,
  useToast,
  Spinner,
  Divider,
} from "@chakra-ui/react";
import { ArrowLeft, Save, Copy } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { RetellWebClient } from "retell-client-js-sdk";
import {
  agentService,
  type Agent,
  type RetellLLM,
  type VoiceDetails,
  type UpdateRetellLLMParams,
} from "../../voice-services/agentService";
import { ModelSelect } from "./ModelSelect";
import { VoiceSelect } from "./VoiceSelect";
import { LanguageSelect } from "./LanguageSelect";
import { WelcomeMessageSelect } from "./WelcomeMessageSelect";
import { callService } from "../../voice-services/callService";
import { WebhookSettings } from "./WebhookSettings";
import { SecuritySettings } from "./SecuritySettings";
import { CallSettings } from "./call-settings/CallSettings";
import { PostCallAnalysis } from "./post-call-analysis/PostCallAnalysis";
import { SpeechSettings } from "./speech-settings/SpeechSettings";
import { AgentKnowledgeBase } from "./knowledge-base/AgentKnowledgeBase";
import { AgentFunctionsWrapper } from "./functions/AgentFunctionsWrapper";
import { DynamicVariablesButton } from "./DynamicVariablesButton";
import { EditableAgentName } from "./EditableAgentName";
import PromptTreeEditor from "./PromptTreeEditor";

interface MultiPromptAgentConfigProps {
  agent: Agent;
}

function getShortId(id: string) {
  if (!id) return "";
  const len = id.length;
  return len > 5 ? `${id.slice(0, 2)}...${id.slice(-3)}` : id;
}

export const MultiPromptAgentConfig: React.FC<MultiPromptAgentConfigProps> = ({
  agent: initialAgent,
}) => {
  const navigate = useNavigate();
  const toast = useToast();
  const retellWebClientRef = useRef<RetellWebClient | null>(null);

  const [agent, setAgent] = useState<Agent>(initialAgent);
  const [prompt, setPrompt] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [noteVisible, setNoteVisible] = useState(false);
  const [voices, setVoices] = useState<VoiceDetails[]>([]);
  const [selectedLLM, setSelectedLLM] = useState<RetellLLM | null>(null);
  const [selectedVoice, setSelectedVoice] = useState<VoiceDetails | null>(null);
  const [isCallStarting, setIsCallStarting] = useState(false);
  const [callData, setCallData] = useState<{
    accessToken: string;
    callId: string;
  } | null>(null);
  const [isCallActive, setIsCallActive] = useState(false);
  const [dynamicVariables, setDynamicVariables] = useState<
    Record<string, string>
  >({});

  const [showPromptTreeEditor, setShowPromptTreeEditor] = useState(false);

  // THEME-AWARE TOKENS
  const appBg = "gray.50";
  const textColor = "gray.800";
  const subtextColor = "gray.600";
  const panelBg = "white";
  const headerBorder = "gray.200";
  const dividerBorder = headerBorder;

  // Neutral, consistent scrollbar
  const scrollbarStyles = {
    "&::-webkit-scrollbar": { width: "8px", height: "8px" },
    "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "rgba(0,0,0,0.25)",
      borderRadius: "6px",
    },
    scrollbarColor: "rgba(0,0,0,0.25) transparent",
  } as const;

  const llmId =
    agent.response_engine?.type === "retell-llm"
      ? agent.response_engine.llm_id
      : null;

  const isRetellLLMAgent = llmId !== null;

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        const voicesResponse = await agentService.listVoices();
        setVoices(voicesResponse);

        // Only fetch Retell LLM if this agent uses retell-llm
        if (isRetellLLMAgent && llmId) {
          const currentLLM = await agentService.getRetellLLM(llmId);
          setSelectedLLM(currentLLM);
          setPrompt(currentLLM.general_prompt || "");
        } else {
          setSelectedLLM(null);
          setPrompt("");
        }

        const foundVoice = voicesResponse.find(
          (v) => v.voice_id === agent.voice_id,
        );
        if (foundVoice) setSelectedVoice(foundVoice);
      } catch (error) {
        console.error("Error loading data:", error);
        toast({ title: "Error loading data", status: "error", duration: 3000 });
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, [agent.voice_id, isRetellLLMAgent, llmId, toast]);

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: "Copied to clipboard",
      status: "success",
      duration: 2000,
      isClosable: true,
    });
  };

  const handleSave = async () => {
    if (!selectedLLM) return;
    setIsSaving(true);
    try {
      const params: UpdateRetellLLMParams = { general_prompt: prompt };
      const updated = await agentService.updateRetellLLM(
        selectedLLM.llm_id,
        params,
      );
      setSelectedLLM(updated);
      toast({
        title: "Prompt saved successfully",
        status: "success",
        duration: 3000,
      });
    } catch (err: any) {
      console.error(err);
      toast({
        title: "Error saving prompt",
        description: err.message,
        status: "error",
        duration: 5000,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const memoizedWelcomeMessageSelect = useMemo(() => {
    if (!selectedLLM) return null;
    return (
      <WelcomeMessageSelect
        initialLLM={selectedLLM}
        agentId={agent.agent_id}
        onLLMUpdate={(updatedLLM: RetellLLM) => setSelectedLLM(updatedLLM)}
      />
    );
  }, [selectedLLM, agent.agent_id]);

  const promptTreeComponent = useMemo(() => {
    return (
      <Box
        mt={4}
        border="1px solid"
        borderColor={headerBorder}
        borderRadius="lg"
        position="relative"
        h="200px"
        w="full"
        display="flex"
        alignItems="center"
        justifyContent="center"
        cursor="pointer"
        bg={panelBg}
      >
        <img
          src={`${process.env.PUBLIC_URL}/tree_preview.webp`}
          alt="Prompt Tree"
          style={{ height: "200px", objectFit: "contain", opacity: 0.8 }}
        />
        <Button
          position="absolute"
          inset="0"
          m="auto"
          h="fit-content"
          w="fit-content"
          colorScheme="primary"
          variant="outline"
          onClick={() => setShowPromptTreeEditor(true)}
          isDisabled={!isRetellLLMAgent}
          title={
            !isRetellLLMAgent
              ? "Prompt tree is only available for Retell LLM agents"
              : undefined
          }
        >
          Edit Prompt Tree
        </Button>
      </Box>
    );
  }, [headerBorder, panelBg, isRetellLLMAgent]);

  const initializeRetellWebClient = useCallback(() => {
    if (!retellWebClientRef.current) {
      retellWebClientRef.current = new RetellWebClient();
      retellWebClientRef.current.on("call_started", () =>
        setIsCallActive(true),
      );
      retellWebClientRef.current.on("call_ended", () => {
        setIsCallActive(false);
        setCallData(null);
      });
      retellWebClientRef.current.on("error", (error) => {
        console.error("An error occurred:", error);
        toast({
          title: "Call Error",
          description:
            (error as any).response?.data?.message ||
            "An error occurred during the call",
          status: "error",
          duration: 5000,
        });
        retellWebClientRef.current?.stopCall();
      });
    }
  }, [toast]);

  const joinCall = useCallback(
    async (accessToken: string) => {
      if (!retellWebClientRef.current) initializeRetellWebClient();
      try {
        await retellWebClientRef.current?.startCall({
          accessToken,
          sampleRate: 24000,
        });
        toast({
          title: "Joined call successfully",
          status: "success",
          duration: 3000,
        });
      } catch (error) {
        console.error("Error joining call:", error);
        toast({
          title: "Error joining call",
          description:
            error instanceof Error
              ? error.message
              : "An unknown error occurred",
          status: "error",
          duration: 5000,
        });
      }
    },
    [initializeRetellWebClient, toast],
  );

  const handleStartCall = useCallback(async () => {
    setIsCallStarting(true);
    try {
      const callOptions: any = {};
      if (Object.keys(dynamicVariables).length > 0) {
        callOptions.retell_llm_dynamic_variables = dynamicVariables;
      }
      const response = await callService.createWebCall(
        agent.agent_id,
        callOptions,
      );
      const newCallData = {
        accessToken: response.access_token,
        callId: response.call_id,
      };
      setCallData(newCallData);
      toast({
        title: "Web call created",
        description: `Call ID: ${response.call_id}`,
        status: "success",
        duration: 5000,
      });
      await joinCall(response.access_token);
    } catch (error) {
      console.error("Error starting web call:", error);
      toast({
        title: "Error starting web call",
        description:
          error instanceof Error ? error.message : "An unknown error occurred",
        status: "error",
        duration: 5000,
      });
    } finally {
      setIsCallStarting(false);
    }
  }, [agent.agent_id, toast, joinCall, dynamicVariables]);

  const handleEndCall = useCallback(() => {
    retellWebClientRef.current?.stopCall();
    setIsCallActive(false);
    setCallData(null);
  }, []);

  const handleDynamicVariablesChange = useCallback(
    (vars: Record<string, string>) => {
      if (Object.keys(vars).length > 0) console.log("Variables updated:", vars);
      setDynamicVariables(vars);
    },
    [],
  );

  const handleVoiceChangeFromModel = useCallback(
    async (voiceId: string) => {
      const voice = voices.find((v) => v.voice_id === voiceId);
      if (!voice) throw new Error("Voice not found");

      await agentService.update(agent.agent_id, {
        voice_id: voiceId,
        voice_model: null,
      });

      setSelectedVoice(voice);
    },
    [voices, agent.agent_id],
  );

  const testPanel = useMemo(
    () => (
      <VStack
        w="250px"
        p={4}
        borderLeft="1px solid"
        borderColor={dividerBorder}
        spacing={4}
        bg={panelBg}
      >
        <Flex align="center" justify="space-between" w="full">
          <Text
            px={3}
            py={1}
            rounded="md"
            fontWeight="semibold"
            color={textColor}
          >
            Test your agent
          </Text>
          <DynamicVariablesButton
            onVariablesChange={handleDynamicVariablesChange}
            prompt={prompt}
            beginMessage={selectedLLM?.begin_message || ""}
          />
        </Flex>

        <Divider borderColor={dividerBorder} />

        <Box textAlign="center" py={8}>
          <Box as="span" fontSize="48px">
            🎙️
          </Box>
          <Button
            mt={4}
            w="full"
            onClick={isCallActive ? handleEndCall : handleStartCall}
            isLoading={isCallStarting}
            loadingText="Starting call..."
            colorScheme="primary"
            bg="primary.600"
            _hover={{ bg: "primary.700" }}
            variant="solid"
          >
            {isCallActive ? "End Call" : "Start Call"}
          </Button>
        </Box>
      </VStack>
    ),
    [
      dividerBorder,
      panelBg,
      textColor,
      isCallActive,
      handleEndCall,
      handleStartCall,
      isCallStarting,
      handleDynamicVariablesChange,
      prompt,
      selectedLLM?.begin_message,
    ],
  );

  return isLoading ? (
    <Flex h="100vh" justify="center" align="center" bg={appBg}>
      <Spinner size="xl" color="primary.500" />
    </Flex>
  ) : (
    <Flex h="100vh" bg={appBg} color={textColor} direction="column">
      {!showPromptTreeEditor && (
        <>
          <Flex
            px={4}
            py={3}
            borderBottom="1px solid"
            borderColor={headerBorder}
            align="center"
            bg={panelBg}
          >
            <IconButton
              aria-label="Back"
              icon={<ArrowLeft />}
              variant="ghost"
              onClick={() => navigate("/voice-agents")}
            />
            <Box ml={4}>
              <EditableAgentName
                agentId={agent.agent_id}
                initialName={agent.agent_name || "Untitled Agent"}
              />
              <Text color={subtextColor} fontSize="sm">
                Multi prompt &bull; Agent ID: {getShortId(agent.agent_id)}{" "}
                <IconButton
                  aria-label="Copy Agent ID"
                  icon={<Copy size={12} />}
                  size="xs"
                  variant="ghost"
                  onClick={() => copyToClipboard(agent.agent_id)}
                />
                {isRetellLLMAgent && llmId ? (
                  <>
                    &nbsp;&bull;&nbsp;LLM ID: {getShortId(llmId)}{" "}
                    <IconButton
                      aria-label="Copy LLM ID"
                      icon={<Copy size={12} />}
                      size="xs"
                      variant="ghost"
                      onClick={() => copyToClipboard(llmId)}
                    />
                  </>
                ) : (
                  <>
                    &nbsp;&bull;&nbsp;LLM:{" "}
                    {agent.response_engine.type === "custom-llm"
                      ? "Custom LLM"
                      : "N/A"}
                  </>
                )}
              </Text>
            </Box>
          </Flex>

          <Flex
            px={4}
            py={2}
            borderBottom="1px solid"
            borderColor={headerBorder}
            gap={2}
            bg={panelBg}
          >
            <ModelSelect
              initialLLM={selectedLLM}
              agentId={agent.agent_id}
              setSelectedLLM={setSelectedLLM}
              currentVoice={selectedVoice}
              onVoiceChange={handleVoiceChangeFromModel}
            />
            <VoiceSelect
              initialVoice={selectedVoice}
              voices={voices}
              agentId={agent.agent_id}
              setSelectedVoice={setSelectedVoice}
              currentModel={selectedLLM}
            />
            <LanguageSelect
              initialLanguage={agent.language}
              agentId={agent.agent_id}
            />
          </Flex>
        </>
      )}

      <Flex flex={1} overflow="hidden" alignItems="stretch">
        {showPromptTreeEditor ? (
          <PromptTreeEditor
            onBack={() => setShowPromptTreeEditor(false)}
            agentId={agent.agent_id}
          />
        ) : (
          <>
            <Box
              flex={1}
              p={4}
              borderRight="1px solid"
              borderColor={headerBorder}
              bg={panelBg}
            >
              <VStack
                align="stretch"
                h="full"
                spacing={4}
                overflowY="auto"
                css={scrollbarStyles}
              >
                <Box position="relative" flex={0} minH="400px">
                  <Textarea
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    bg="gray.50"
                    border="1px solid"
                    borderColor="gray.200"
                    _focus={{
                      borderColor: "brand.500",
                      boxShadow: "0 0 0 1px var(--chakra-colors-brand-500)",
                    }}
                    resize="none"
                    fontSize="sm"
                    placeholder="Type in a universal prompt for your agent, such as its role, conversational style, objective, etc."
                    pr="100px"
                    h="100%"
                    onMouseEnter={() => setNoteVisible(true)}
                    onMouseLeave={() => setNoteVisible(false)}
                    isDisabled={!isRetellLLMAgent}
                  />
                  <Button
                    leftIcon={<Save size={16} />}
                    onClick={handleSave}
                    isLoading={isSaving}
                    position="absolute"
                    bottom="8px"
                    right="8px"
                    size="sm"
                    colorScheme="primary"
                    bg="primary.600"
                    _hover={{ bg: "primary.700" }}
                    isDisabled={!isRetellLLMAgent || !selectedLLM}
                  >
                    Save
                  </Button>
                  {noteVisible && (
                    <Text
                      position="absolute"
                      bottom="8px"
                      left="8px"
                      fontSize="xs"
                      color={subtextColor}
                    >
                      Use `{"{{_}}"}` to add variables.
                    </Text>
                  )}
                </Box>

                {memoizedWelcomeMessageSelect}
                {promptTreeComponent}
              </VStack>
            </Box>

            <VStack
              w="300px"
              spacing={0}
              overflow="auto"
              p={4}
              borderRight="1px solid"
              borderColor={headerBorder}
              css={scrollbarStyles}
              bg={panelBg}
            >
              <Accordion allowMultiple w="full">
                {isRetellLLMAgent && llmId ? (
                  <>
                    <AgentFunctionsWrapper
                      agentId={agent.agent_id}
                      llmId={llmId}
                      initialFunctions={selectedLLM?.general_tools || []}
                    />
                    <AgentKnowledgeBase
                      agentId={agent.agent_id}
                      llmId={llmId}
                      initialLLM={selectedLLM}
                      onLLMUpdate={setSelectedLLM}
                    />
                  </>
                ) : null}

                <SpeechSettings agentId={agent.agent_id} />
                <CallSettings
                  agentId={agent.agent_id}
                  initialSettings={{
                    enable_voicemail_detection:
                      agent.enable_voicemail_detection,
                    end_call_after_silence_ms: agent.end_call_after_silence_ms,
                    max_call_duration_ms: agent.max_call_duration_ms,
                    begin_message_delay_ms: agent.begin_message_delay_ms,
                  }}
                />
                <PostCallAnalysis
                  agentId={agent.agent_id}
                  initialAnalysisData={agent.post_call_analysis_data || null}
                />

                <SecuritySettings
                  agentId={agent.agent_id}
                  initialOptOut={
                    agent.data_storage_setting === "basic_attributes_only"
                  }
                  initialFallbackVoiceIds={agent.fallback_voice_ids || null}
                />

                <WebhookSettings
                  agentId={agent.agent_id}
                  initialWebhookUrl={agent.agent_webhook_url}
                />
              </Accordion>
            </VStack>
          </>
        )}

        {testPanel}
      </Flex>
    </Flex>
  );
};
